// RegisterQrCodeStep1.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../components/AuthProvider';
import { LayoutContext } from '../components/LayoutProvider';
import { showTemporaryMessage } from "../components/Message";
import {
    get_emergency_contacts,
    create_emergency_contact
} from "../tools-network";
import NeumorphicContainer from './NeumorphicContainer';

const RegisterQrCodeStep1 = ({ onNext }) => {
    const { t } = useTranslation();
    const { userType, loginToken, userId } = React.useContext(AuthContext);
    const { handleInvalidToken } = React.useContext(AuthContext);
    const { setIsHeaderVisible } = React.useContext(LayoutContext);

    const [phoneNumber, setPhoneNumber] = useState('');
    const [description, setDescription] = useState('');
    const [hasExistingContact, setHasExistingContact] = useState(false);
    const [initialDataLoaded, setInitialDataLoaded] = useState(false);

    const isValidPhone = (phone) => {
        if (!phone || !phone.trim()) return false;
        const cleaned = phone.replace(/\s+/g, '');
        return /^(07\d{8}|\+467\d{8})$/.test(cleaned);
    };

    useEffect(() => {
        setIsHeaderVisible(false);

        return () => setIsHeaderVisible(true);
    }, [setIsHeaderVisible]);

    useEffect(() => {
        checkEmergencyContacts();
    }, []);

    const handleNext = async () => {
        // Validate phone and description
        if (!isValidPhone(phoneNumber)) {
            showTemporaryMessage(t("invalid_phone_number"), t("phone_format_help"), 'warning');
            return;
        }
        if (!description.trim()) {
            showTemporaryMessage(t("description_required"), t("please_enter_contact_description"), 'warning');
            return;
        }
        
        // Create new emergency contact
        try {
            const res = await create_emergency_contact(loginToken, phoneNumber, description);
            if (res.success) {
                showTemporaryMessage(t('emergency_contact_added'), '', 'success');
                onNext();
            } else {
                showTemporaryMessage(t('failed_to_add_contact'), res.msg, 'danger');
            }
        } catch (err) {
            showTemporaryMessage(t('error_adding_contact'), String(err), 'danger');
        }
    };

    const checkEmergencyContacts = async () => {
        try {
            const res = await get_emergency_contacts(loginToken);
            if (res.success) {
                const contacts = res.data || [];
                const primaryContact = contacts.find(c => c.is_primary);
                if (primaryContact) {
                    // Auto-advance if primary contact exists
                    onNext();
                } else {
                    // Show form to create primary contact
                    setInitialDataLoaded(true);
                }
            } else {
                showTemporaryMessage(t('failed_to_load_emergency_contacts'), res.msg, 'danger');
                handleInvalidToken();
            }
        } catch (err) {
            showTemporaryMessage(t('error_loading_emergency_contacts'), String(err), 'danger');
            handleInvalidToken();
        }
    };

    return (
        <>
            {!initialDataLoaded &&
                <div className="flex w-full h-full items-center justify-center text-xl italic font-semibold text-purple loading-animation">{t("loading_emergency_contacts")}</div>
            }
            {initialDataLoaded && (
                <NeumorphicContainer>
                    <h2 className="text-center text-2xl leading-tight px-2 mb-4">
                        {t("register_primary_contact")}
                    </h2>
                    <div className="space-y-3 mb-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('description')} *
                            </label>
                            <input
                                type="text"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder={t('description_placeholder')}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-cyan-500 focus:border-cyan-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('phone_number')} *
                            </label>
                            <input
                                type="tel"
                                value={phoneNumber}
                                onChange={(e) => setPhoneNumber(e.target.value)}
                                placeholder="070 123 45 67"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-cyan-500 focus:border-cyan-500"
                            />
                            <p className="text-xs text-gray-500 mt-1">{t('phone_format_help')}</p>
                        </div>
                    </div>
                    <div className="flex justify-center">
                        <button
                            onClick={handleNext}
                            disabled={!isValidPhone(phoneNumber) || !description.trim()}
                            className="btn-main btn-main:hover text-white px-6 py-2 w-auto rounded-md sm:leading-6 text-center font-semibold text-sm transition ease-in-out hover:scale-105 disabled:bg-gray-400 disabled:text-grey-600 disabled:cursor-not-allowed"
                        >
                            {t("next")}
                        </button>
                    </div>
                </NeumorphicContainer>
            )}
        </>
    );
}

export default RegisterQrCodeStep1;